import { useEffect, useState } from "react";

import BottomNavigation from "../../Components/BottomNavigation";
import SearchContainer from "../../Components/SearchContainer";
import NoDataFound from "../../Components/NoData";
import Loader from "../../Components/Loader";

import "../../assets/style/TransactionsStyles.scss";

// PrimeReact imports
import { Calendar } from "primereact/calendar";

import { Language } from "../../Services/GlobalData";
import { getData } from "../../Services/Util";
import {
  fetchDownloadTransaction,
  fetchOldInvoices,
} from "../../Services/AppAction";
import { useAlert } from "../../Modal/AlertProviderModal";
import { encode } from "base-64";
import { trackEvent } from "../../Services/analytics";
import { printHtml } from "../../Services/printHtml";
import Header from "../../Components/Header";
import { useNavigate } from "react-router-dom";
import { formatIndianPrice } from "../../Services/Constants";

// This screen only deals with Invoices — the type selector grid and hero
// banner from TransactionsPage are intentionally not carried over here.
const TYPE_CODE = "INV";
const TYPE_META = {
  icon: "pi-file",
  iconBg: "#e8f5e9",
  iconColor: "#009343",
};

const OldInvoicesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "DUE" | "PAID">(
    "ALL"
  );
  const [parentId, setParentId] = useState("");

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { showAlert } = useAlert();

  // ─── Init ───────────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      const today = new Date();
      const back30 = new Date();
      back30.setDate(today.getDate() - 30);
      setStartDate(back30);
      setEndDate(today);
    };
    init();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      loadTransactions();
    }
  }, [startDate, endDate]);

  // ─── Load Transactions ───────────────────────────────────────────

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const userData = await getData("userData");

      if (!startDate || !endDate) {
        return;
      }

      const startStr = startDate
        ?.toLocaleDateString("en-GB")
        .split("/")
        .join("-");
      const endStr = endDate?.toLocaleDateString("en-GB").split("/").join("-");

      const res = await fetchOldInvoices(TYPE_CODE, startStr, endStr);
     // console.log("Track@ Response: ", res);
      if (res?.status === 0 && Array.isArray(res.data)) {
        trackEvent(
          "old_invoices_loaded",
          { type: TYPE_CODE, record_count: res.data?.length },
          userData?.PARTY_ID,
          userData?.mobileNumber
        );
        setParentId(res.data[0].PID);
        const mapped = res.data.map((item: any) => ({
          TransactionId: item.INVH_NO,
          date: new Date(item.INVH_DATE),
          amount: item.INVH_TOT_AMT,
          outstanding: item.OUTSTANDING_AMT || 0,
          volume: item.NET_VOL,
          type: "Invoice",
          status: item.STATUS,
          startdate: item.INVH_DATE,
          downloadId: item.INVH_ID,
          customerCode: item.CUST_CODE,
          companyName: item.COMP_NAME,
          companyId: item.COMP_ID
        })).filter(Boolean);

      //  console.log("Track@ Mapped Response: ", mapped);

        setTransactions(mapped);
        setFilteredTransactions(mapped);
        setStatusFilter("ALL");
      } else {
        trackEvent(
          "old_invoices_load_failed",
          { type: TYPE_CODE, record_count: res.data?.length },
          userData?.PARTY_ID,
          userData?.mobileNumber
        );
        setTransactions([]);
        setFilteredTransactions([]);
      }
    } catch (error) {
      console.error("Error fetching old invoices:", error);
      showAlert("error", "Failed to load old invoices.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Download ────────────────────────────────────────────────────

  const handleDownload = async (id: string) => {
    try {
      setLoading(true);
      const userData = await getData("userData");

      const payload = {
        invHdrId: encode(id),
        custId: encode(userData.PARTY_ID),
        invtype: encode(TYPE_CODE),
      };

      const response = await fetchDownloadTransaction(payload);

      if (response.status !== 0 || !response.data?.[0]?.HTMLContent) {
        showAlert("error", "No data found");
        trackEvent(
          "old_invoice_download_failed",
          { transaction_id: id, type: TYPE_CODE },
          userData?.PARTY_ID,
          userData?.mobileNumber
        );
        return;
      }

      printHtml(response.data[0].HTMLContent, `invoice_${id}`);
      trackEvent(
        "old_invoice_downloaded",
        { transaction_id: id, type: TYPE_CODE },
        userData?.PARTY_ID,
        userData?.mobileNumber
      );
      showAlert("success", "File opened for download");
    } catch (err) {
      showAlert("error", "Failed to download invoice");
    } finally {
      setLoading(false);
    }
  };

  // ─── Helpers ─────────────────────────────────────────────────────

  const formatDateDisplay = (date: string | Date) => {
    const d = typeof date === "string" ? new Date(date) : date;
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const formatAmount = (amt: number | string) =>
    `₹ ${Number(amt)?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  const getMaxEndDate = (): Date => {
    if (!startDate) {
      return new Date();
    }
    const maxEnd = new Date(startDate);
    maxEnd.setDate(maxEnd.getDate() + 365);
    const today = new Date();
    return maxEnd < today ? maxEnd : today;
  };

  const handleSearch = async (text: string) => {
    setSearchText(text);
    applyFilters(text, statusFilter);
  };

  const applyFilters = (search: string, status: "ALL" | "DUE" | "PAID") => {
    let result = [...transactions];
    if (search.trim()) {
      result = result.filter((t) =>
        t.TransactionId.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (status === "DUE") {
      result = result.filter((t) => t.status === "DUE");
    }
    if (status === "PAID") {
      result = result.filter((t) => t.status === "PAID");
    }
    setFilteredTransactions(result);
  };

  const handleStatusFilter = (status: "ALL" | "DUE" | "PAID") => {
    setStatusFilter(status);
    applyFilters(searchText, status);
  };

  const handleStartDateChange = async (date: Date | null) => {
    if (!date) {
      return;
    }
    // End date stays exactly as the user selected it — only enforce the
    // 365-day max gap between start and end.
    if (endDate) {
      const diff =
        Math.abs(endDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
      if (diff > 365) {
        const adjusted = new Date(date);
        adjusted.setDate(adjusted.getDate() + 365);
        const today = new Date();
        setEndDate(adjusted > today ? today : adjusted);
      }
    }
    setStartDate(date);
  };

  const handleEndDateChange = async (date: Date | null) => {
    if (!date) {
      return;
    }
    if (startDate) {
      const diff =
        Math.abs(date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
      if (diff > 365) {
        showAlert("warning", "Date range cannot exceed 365 days.");
        return;
      }
    }
    setEndDate(date);
  };

  // ─── Computed KPIs ───────────────────────────────────────────────

  const totalOutstanding = transactions.reduce(
    (s, t) => s + Number(t.outstanding || 0),
    0
  );
  const totalAmount = transactions.reduce(
    (s, t) => s + Number(t.amount || 0),
    0
  );
  const dueCount = transactions.filter((t) => t.status === "DUE").length;
  const paidCount = transactions.filter((t) => t.status === "PAID").length;

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <div className="trx-page">
      <Header
        isShowBack
        navigate={navigate}
        goBack={() => {}}
        title={"All Invoices"}
      />

      {/* Search */}
      <div className="trx-search-wrap">
        <SearchContainer
          placeholder={"Search Invoices"}
          onChangeText={handleSearch}
        />
      </div>

      {/* ── Hero Banner ── */}
      <div className="trx-hero">
        <div className="trx-hero-text">
          <h2 className="trx-hero-title">{"All Invoices"}</h2>
          <p className="trx-hero-sub">{"All invoices under Parent ID"}</p>
        </div>
        <img
          src={require("../../assets/images/transactions-header.png")}
          className="trx-hero-img"
          alt="transactions"
        />
      </div>

      {/* ── Parent ID Banner ── */}
      <div className="trx-parent-id-card">
        <span className="trx-parent-id-label">Parent ID</span>
        <div className="trx-parent-id-row">
          <span className="trx-parent-id-value">{parentId}</span>
          <button
            className="trx-parent-id-copy"
            onClick={() => navigator.clipboard.writeText(parentId)}
          >
            <i className="pi pi-copy" />
          </button>
        </div>
        <span className="trx-parent-id-desc">
          All invoices linked to customers under this Parent ID
        </span>
      </div>

      <div className="trx-scroll">
        {/* ── KPI Stats Row ── */}
        <div className="trx-kpi-row">
          <div className="trx-kpi-card">
            <div
              className="trx-kpi-icon-wrap"
              style={{ background: "#e8f5e9" }}
            >
              <i
                className="pi pi-list trx-kpi-icon"
                style={{ color: "#009343" }}
              />
            </div>
            <span className="trx-kpi-label">Total Invoices</span>
            <span className="trx-kpi-value">{transactions.length}</span>
          </div>
          <div className="trx-kpi-card">
            <div
              className="trx-kpi-icon-wrap"
              style={{ background: "#f3e5f5" }}
            >
              <i
                className="pi pi-calendar trx-kpi-icon"
                style={{ color: "#6a1b9a" }}
              />
            </div>
            <span className="trx-kpi-label">Total Amount</span>
            <span className="trx-kpi-value">
              ₹{" "}
              {formatIndianPrice(totalAmount?.toLocaleString("en-IN", {
                maximumFractionDigits: 2,
              }))}
            </span>
          </div>
          <div className="trx-kpi-card">
            <div
              className="trx-kpi-icon-wrap"
              style={{ background: "#fff8e1" }}
            >
              <i
                className="pi pi-indian-rupee trx-kpi-icon"
                style={{ color: "#f9a825" }}
              />
            </div>
            <span className="trx-kpi-label">Outstanding</span>
            <span className="trx-kpi-value trx-kpi-value--due">
              ₹{" "}
              {formatIndianPrice(totalOutstanding.toLocaleString("en-IN", {
                maximumFractionDigits: 2,
              }))}
            </span>
          </div>
        </div>

        {/* ── Date Filters ── */}
        <div className="trx-date-row">
          <div className="trx-date-item">
            <span className="trx-date-label">Start Date</span>
            <div className="trx-calendar-wrap">
              <i className="pi pi-calendar trx-calendar-icon" />
              <Calendar
                value={startDate}
                onChange={(e) => handleStartDateChange(e.value as Date | null)}
                dateFormat="dd M yy"
                placeholder={Language.Lbl_StDate}
                maxDate={new Date()}
                showIcon={false}
                readOnlyInput
                inputClassName="trx-calendar-input"
                panelClassName="trx-calendar-panel"
                className="trx-calendar"
              />
              <i className="pi pi-chevron-down trx-calendar-chevron" />
            </div>
          </div>
          <div className="trx-date-item">
            <span className="trx-date-label">End Date</span>
            <div className="trx-calendar-wrap">
              <i className="pi pi-calendar trx-calendar-icon" />
              <Calendar
                value={endDate}
                onChange={(e) => handleEndDateChange(e.value as Date | null)}
                dateFormat="dd M yy"
                placeholder={Language.Lbl_EndDate}
                maxDate={getMaxEndDate()}
                minDate={startDate ?? undefined}
                showIcon={false}
                readOnlyInput
                inputClassName="trx-calendar-input"
                panelClassName="trx-calendar-panel"
                className="trx-calendar"
              />
              <i className="pi pi-chevron-down trx-calendar-chevron" />
            </div>
          </div>
        </div>

        {/* ── Status sub-tabs: All / Due / Paid ── */}
        <div className="trx-status-tabs">
          <button
            className={`trx-status-tab ${
              statusFilter === "ALL" ? "trx-status-tab--all-active" : ""
            }`}
            onClick={() => handleStatusFilter("ALL")}
          >
            All ({transactions.length})
          </button>
          <button
            className={`trx-status-tab ${
              statusFilter === "DUE" ? "trx-status-tab--due-active" : ""
            }`}
            onClick={() => handleStatusFilter("DUE")}
          >
            Due ({dueCount})
          </button>
          <button
            className={`trx-status-tab ${
              statusFilter === "PAID" ? "trx-status-tab--paid-active" : ""
            }`}
            onClick={() => handleStatusFilter("PAID")}
          >
            Paid ({paidCount})
          </button>
        </div>

        {/* ── Transaction Cards ── */}
        <div className="trx-list">
          {filteredTransactions.length === 0 ? (
            <div className="trx-empty">
              <NoDataFound />
            </div>
          ) : (
            filteredTransactions.map((transaction, index) => {
              if (!transaction) {
                return null;
              }
              const status = transaction.status as string;
              const statusLower = status?.toLowerCase();
              const isPaid = statusLower === "paid";
              const isDue = statusLower === "due";
              const isCancelled = statusLower === "cancelled";

              const stripColor = isPaid
                ? "#009343"
                : isDue
                ? "#f9a825"
                : isCancelled
                ? "#ef4444"
                : "#9ca3af";
              const badgeBg = isPaid
                ? "#e8f5e9"
                : isDue
                ? "#fff8e1"
                : isCancelled
                ? "#fce4ec"
                : "#f3f4f6";
              const badgeColor = isPaid
                ? "#009343"
                : isDue
                ? "#e65100"
                : isCancelled
                ? "#c62828"
                : "#6b7280";

              return (
                <div key={`invoice-${index}`} className="trx-card">
                  {/* Left color strip */}
                  <div
                    className="trx-card-strip"
                    style={{ background: stripColor }}
                  />

                  <div className="trx-card-body">
                    {/* Top row: icon + id/type + date + badge */}
                    <div className="trx-card-top">
                      <div
                        className="trx-card-icon-wrap"
                        style={{ background: TYPE_META.iconBg }}
                      >
                        <i
                          className={`pi ${TYPE_META.icon} trx-card-icon`}
                          style={{ color: TYPE_META.iconColor }}
                        />
                      </div>
                      <div className="trx-card-id-col">
                        <span className="trx-card-id">
                          {transaction.TransactionId}
                        </span>
                        <span className="trx-card-type">
                          {transaction.type}
                        </span>
                      </div>
                      <div className="trx-card-right">
                        <span className="trx-card-date">
                          {formatDateDisplay(transaction.startdate)}
                        </span>
                        <span
                          className="trx-card-badge"
                          style={{ background: badgeBg, color: badgeColor }}
                        >
                          {status}
                        </span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="trx-card-divider" />

                    {/* Bottom row: amount | outstanding | download */}
                    <div className="trx-card-bottom">
                      <div className="trx-card-stat">
                        <span className="trx-card-stat-label">
                          Total Amount
                        </span>
                        <span className="trx-card-stat-value trx-card-stat-value--green">
                          {formatAmount(Number(transaction.amount).toFixed(2))}
                        </span>
                      </div>

                      <div className="trx-card-stat-divider" />
                      <div className="trx-card-stat">
                        <span className="trx-card-stat-label">Outstanding</span>
                        <span className="trx-card-stat-value trx-card-stat-value--orange">
                          {formatAmount(
                            Number(transaction.outstanding).toFixed(2)
                          )}
                        </span>
                      </div>

                      {!isCancelled && (
                        <button
                          className="trx-download-btn"
                          onClick={() => handleDownload(transaction.downloadId)}
                        >
                          <div className="trx-download-icon-wrap">
                            <i className="pi pi-download" />
                          </div>
                          <span className="trx-download-label">Download</span>
                        </button>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="trx-card-divider" />

                    {/* Meta row: customer code | distributor code | distributor name */}
                    <div className="trx-card-meta">
                      <div className="trx-card-meta-item">
                        <span className="trx-card-meta-label">
                          Customer Code
                        </span>
                        <span className="trx-card-meta-value">{transaction.customerCode}</span>
                      </div>
                      <div className="trx-card-meta-item">
                        <span className="trx-card-meta-label">
                          Distributor Code
                        </span>
                        <span className="trx-card-meta-value">{transaction.companyId}</span>
                      </div>
                      <div className="trx-card-meta-item trx-card-meta-item--name">
                        <span className="trx-card-meta-label">
                          Distributor Name
                        </span>
                        <span className="trx-card-meta-value trx-card-meta-value--name">
                          {transaction.companyName}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {loading && <Loader loading={loading} />}
    </div>
  );
};

export default OldInvoicesPage;
